<template>
  <div class="print-receipt-host">
    <div v-if="bet" ref="receipt" class="print-receipt">

      <div v-if="skinLogoUrl" class="pr-logo">
        <img :src="skinLogoUrl" alt="logo" />
      </div>

      <div class="pr-header">
        <div class="pr-datetime">{{ printedAt }}</div>
        <div class="pr-id">{{ rCode }}</div>
      </div>

      <div class="pr-events">
        <div v-for="(group, i) in groupedOdds" :key="i" class="pr-event">
          <div class="pr-match">{{ group.match }}</div>
          <div class="pr-desc">{{ group.desc }}</div>

          <template v-if="group.isBetBuilder">
            <div v-for="(o, j) in group.odds" :key="j" class="pr-bb-odd">
              <span>{{ o.outcome_alias || o.outcome_name }} ({{ o.market_alias || o.market_name }})</span>
              <span class="pr-odd-val">@{{ fmt2(o.bb_original_odd_value || o.outcome_odd_value) }}</span>
            </div>
            <div class="pr-line">
              <span>{{ fmtDate(group.time) }}</span>
              <span>Q: <b>{{ fmt2(group.bbOdd) }}</b></span>
            </div>
          </template>

          <template v-else>
            <div class="pr-sign">{{ group.sign }}</div>
            <div class="pr-line">
              <span>{{ fmtDate(group.time) }}</span>
              <span class="pr-odd-val">@{{ fmt2(group.odd) }}</span>
            </div>
          </template>
        </div>
      </div>

      <div class="pr-footer">
        <div class="pr-row">
          <span>{{ $t(bet.type) }}</span>
          <span>{{ bet.odds ? bet.odds.length : 0 }} ev.</span>
        </div>
        <div v-if="rIsIntegral" class="pr-row">
          <span>{{ $t('combinations') || 'Combinazioni' }}</span>
          <span>{{ rCombinations }} x {{ rMultiplicity }}</span>
        </div>
        <div v-if="rIsSystem" class="pr-row">
          <span>{{ $t('combinations') || 'Combinazioni' }}</span>
          <span>{{ rCombinations }}</span>
        </div>
        <div class="pr-divider"></div>
        <div class="pr-row">
          <span>{{ $t('amount') }}</span>
          <span>{{ currencySymbol }} {{ fmt2(rAmount) }}</span>
        </div>
        <!-- single / multiple -->
        <div class="pr-row" v-if="!rIsRange && rOddsTotal > 0">
          <span>{{$t('odd_total')}}</span>
          <span>{{ fmt2(rOddsTotal) }}</span>
        </div>
        <!-- integral: quota range (system has no quota) -->
        <div class="pr-row" v-if="rIsIntegral">
          <span>{{$t('odd_total')}}</span>
          <span>{{ fmt2(rOddsTotalMin) }} - {{ fmt2(rOddsTotalMax) }}</span>
        </div>
        <div class="pr-row" v-if="rBonusPct > 0">
          <span>{{ $t('bonus') }} {{ rBonusPct }}%</span>
          <span>{{ currencySymbol }} {{ fmt2(rBonus) }}<template v-if="rIsIntegral && rBonusMax > rBonus"> - {{ fmt2(rBonusMax) }}</template></span>
        </div>
        <!-- single / multiple winning -->
        <div v-if="!rIsRange" class="pr-row pr-row--total">
          <span>{{ $t('winning') }} {{ $t('total') }}</span>
          <span>{{ currencySymbol }} {{ fmt2(rWinningTotal) }}</span>
        </div>
        <!-- integral / system: winning range on two rows -->
        <template v-if="rIsRange">
          <div class="pr-row">
            <span>{{ $t('winning') }} min.</span>
            <span>{{ currencySymbol }} {{ fmt2(rWinningMin) }}</span>
          </div>
          <div class="pr-row pr-row--total">
            <span>{{ $t('winning') }} max.</span>
            <span>{{ currencySymbol }} {{ fmt2(rWinningMax) }}</span>
          </div>
        </template>
        <div class="pr-barcode-svg" v-html="barcodeSvg"></div>
      </div>

    </div>
  </div>
</template>

<script>
import JsBarcode from 'jsbarcode';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

export default {
  name: 'PrintReceiptBet',
  props: {
    bet:            { type: Object, default: null },
    currencySymbol: { type: String, default: '' },
  },
  data() {
    return { printedAt: '' };
  },
  computed: {
    skinLogoUrl() {
      return this.$store.getters['settings/getSkinLogoUrl'] || '';
    },
    // Normalizes fields across confirmed bets (flat), _multiple_detail, _integral_detail, _system_detail
    _dm() { return this.bet?._multiple_detail || {}; },
    _di() { return this.bet?._integral_detail || {}; },
    _ds() { return this.bet?._system_detail   || {}; },
    rIsIntegral()  { return this.bet?.type === 'integral'; },
    rIsSystem()    { return this.bet?.type === 'system'; },
    rIsRange()     { return this.rIsIntegral || this.rIsSystem; },
    rCode()        { return this.bet?.bet_code || this.bet?.betCode || this.bet?.code || ''; },
    rAmount() {
      if (this.rIsSystem)   return this._ds.amount_system  ?? this.bet?.amount_real ?? 0;
      if (this.rIsIntegral) return this._di.amount_total   ?? this._di.amount       ?? this.bet?.amount_real ?? 0;
      return this.bet?.amount ?? this._dm.amount ?? this.bet?.amount_real ?? 0;
    },
    rOddsTotal() {
      if (this.rIsRange) return 0; // range types show quota differently
      return this.bet?.odds_total ?? this._dm.odds_total ?? 0;
    },
    rOddsTotalMin() {
      if (this.rIsSystem) return 0; // system has no single quota range
      return this._di.odds_total_min ?? 0;
    },
    rOddsTotalMax() {
      if (this.rIsSystem) return 0;
      return this._di.odds_total_max ?? 0;
    },
    rBonusPct() {
      if (this.rIsSystem)   return 0; // _system_detail has no bonus_percentage
      if (this.rIsIntegral) return this._di.bonus_percentage ?? 0;
      return this.bet?.bonus_percentage ?? this._dm.bonus_percentage ?? 0;
    },
    rBonus() {
      if (this.rIsSystem)   return this._ds.bonus_min ?? 0;
      if (this.rIsIntegral) return this._di.bonus_min ?? 0;
      return this.bet?.bonus ?? this._dm.bonus ?? 0;
    },
    rBonusMax() {
      if (this.rIsSystem)   return this._ds.bonus_max ?? 0;
      return this._di.bonus_max ?? 0;
    },
    rWinningTotal() {
      if (this.rIsRange) return 0;
      return this.bet?.winning_total ?? this._dm.winning_total ?? 0;
    },
    rWinningMin() {
      if (this.rIsSystem)   return this._ds.winning_total_min ?? 0;
      return this._di.winning_total_min ?? 0;
    },
    rWinningMax() {
      if (this.rIsSystem)   return this._ds.winning_total_max ?? 0;
      return this._di.winning_total_max ?? 0;
    },
    rCombinations() {
      if (this.rIsSystem)   return this._ds.combinations ?? 0;
      return this._di.combinations ?? 0;
    },
    rMultiplicity()   { return this._di.multiplicity ?? 0; },
    barcodeSvg() {
      return this._generateBarcodeSvg(this.rCode);
    },
    groupedOdds() {
      const odds = (this.bet && this.bet.odds) || [];
      const groups = [];
      const bbSeen = new Set();
      for (const odd of odds) {
        if (odd.bet_builder_id) {
          if (!bbSeen.has(odd.bet_builder_id)) {
            bbSeen.add(odd.bet_builder_id);
            const bbOdds = odds.filter(o => o.bet_builder_id === odd.bet_builder_id);
            const bbOdd = Math.max(...bbOdds.map(o => parseFloat(o.outcome_odd_value) || 0));
            groups.push({ isBetBuilder: true, match: this._matchStr(odd), desc: odd.event_description, time: odd.time, bbOdd, odds: bbOdds });
          }
        } else {
          groups.push({
            isBetBuilder: false,
            match: this._matchStr(odd),
            desc:  odd.event_description,
            time:  odd.time,
            sign:  (odd.outcome_alias || odd.outcome_name) + ' (' + (odd.market_alias || odd.market_name) + ')',
            odd:   odd.outcome_odd_value,
            odds:  [odd],
          });
        }
      }
      return groups;
    },
  },
  methods: {
    _matchStr(odd) {
      return [
        odd.sport      && odd.sport.name      ? odd.sport.name      : '',
        odd.category   && odd.category.name   ? odd.category.name   : '',
        odd.tournament && odd.tournament.name ? odd.tournament.name : '',
      ].filter(Boolean).join(' > ');
    },

    fmt2(v) {
      const n = parseFloat(v);
      return isNaN(n) ? '0.00' : n.toFixed(2);
    },

    fmtDate(ms) {
      if (!ms) return '';
      return new Date(ms).toLocaleString();
    },

    async triggerDownload() {
      if (!this.bet) return;
      const now = new Date().toLocaleString();
      const receiptHtml = this._buildReceiptHtml(this.bet, now);

      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:220px;border:none;visibility:hidden;';
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(this._popupHtml(receiptHtml));
      doc.close();

      // Wait for iframe resources (fonts, images) to load
      await new Promise(resolve => setTimeout(resolve, 300));

      try {
        const canvas = await html2canvas(doc.body, { scale: 2, useCORS: true, logging: false });
        const imgData = canvas.toDataURL('image/jpeg', 1.0);

        const mmWidth  = 58;
        const mmHeight = (canvas.height * mmWidth) / canvas.width;
        const code     = this.rCode || 'receipt';

        const pdf = new jsPDF({ unit: 'mm', format: [mmWidth, mmHeight], orientation: 'portrait' });
        pdf.addImage(imgData, 'JPEG', 0, 0, mmWidth, mmHeight);
        pdf.save(`Ticket_${code}.pdf`);
      } finally {
        if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
      }
    },

    triggerPrint() {
      if (!this.bet) return;

      // Costruisce l'HTML dai dati (senza leggere il DOM) in modo che funzioni
      // anche se il componente viene smontato subito dopo (v-if="!getConfirmedBet").
      const now = new Date().toLocaleString();
      const receiptHtml = this._buildReceiptHtml(this.bet, now);

      const iframe = document.createElement('iframe');
      iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:1px;height:1px;border:none;';
      document.body.appendChild(iframe);

      const doc = iframe.contentDocument || iframe.contentWindow.document;
      doc.open();
      doc.write(this._popupHtml(receiptHtml));
      doc.close();

      setTimeout(() => {
        try {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        } finally {
          setTimeout(() => {
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
          }, 1000);
        }
      }, 500);
    },

    _buildReceiptHtml(bet, datetime) {
      const t   = (key) => this.$t(key);
      const fmt = (v) => { const n = parseFloat(v); return isNaN(n) ? '0.00' : n.toFixed(2); };
      const fmtDate = (ms) => ms ? new Date(ms).toLocaleString() : '';
      const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      const matchStr = (o) => [o.sport && o.sport.name, o.category && o.category.name, o.tournament && o.tournament.name].filter(Boolean).join(' > ');

      const odds = bet.odds || [];
      const groups = [];
      const bbSeen = new Set();
      for (const odd of odds) {
        if (odd.bet_builder_id) {
          if (!bbSeen.has(odd.bet_builder_id)) {
            bbSeen.add(odd.bet_builder_id);
            const bbOdds = odds.filter(o => o.bet_builder_id === odd.bet_builder_id);
            const bbOdd = Math.max(...bbOdds.map(o => parseFloat(o.outcome_odd_value) || 0));
            groups.push({ isBetBuilder: true, odd, bbOdd, bbOdds });
          }
        } else {
          groups.push({ isBetBuilder: false, odd });
        }
      }

      const eventsHtml = groups.map(group => {
        if (group.isBetBuilder) {
          const bbRows = group.bbOdds.map(o =>
            `<div class="pr-bb-odd"><span>${esc(o.outcome_alias || o.outcome_name)} (${esc(o.market_alias || o.market_name)})</span><span class="pr-odd-val">@${fmt(o.bb_original_odd_value || o.outcome_odd_value)}</span></div>`
          ).join('');
          return `<div class="pr-event"><div class="pr-match">${esc(matchStr(group.odd))}</div><div class="pr-desc">${esc(group.odd.event_description)}</div>${bbRows}<div class="pr-line"><span>${esc(fmtDate(group.odd.time))}</span><span>Q: <b>${fmt(group.bbOdd)}</b></span></div></div>`;
        }
        const o = group.odd;
        const sign = `${esc(o.outcome_alias || o.outcome_name)} (${esc(o.market_alias || o.market_name)})`;
        return `<div class="pr-event"><div class="pr-match">${esc(matchStr(o))}</div><div class="pr-desc">${esc(o.event_description)}</div><div class="pr-sign">${sign}</div><div class="pr-line"><span>${esc(fmtDate(o.time))}</span><span class="pr-odd-val">@${fmt(o.outcome_odd_value)}</span></div></div>`;
      }).join('');

      const isIntegral    = this.rIsIntegral;
      const isSystem      = this.rIsSystem;
      const isRange       = this.rIsRange;
      const rCode         = this.rCode;
      const rAmount       = this.rAmount;
      const rOddsTotal    = this.rOddsTotal;
      const rBonusPct     = this.rBonusPct;
      const rBonus        = this.rBonus;
      const rWinningTotal = this.rWinningTotal;
      const combLabel     = esc(t('combinations') || 'Combinazioni');

      const combinationsRow = isIntegral
        ? `<div class="pr-row"><span>${combLabel}</span><span>${this.rCombinations} x ${this.rMultiplicity}</span></div>`
        : isSystem
          ? `<div class="pr-row"><span>${combLabel}</span><span>${this.rCombinations}</span></div>`
          : '';

      const oddsRow = isIntegral
        ? `<div class="pr-row"><span>${esc(t('odd_total'))}</span><span>${fmt(this.rOddsTotalMin)} - ${fmt(this.rOddsTotalMax)}</span></div>`
        : (!isRange && rOddsTotal > 0) ? `<div class="pr-row"><span>${esc(t('odd_total'))}</span><span>${fmt(rOddsTotal)}</span></div>` : '';

      const bonusRange = isRange && this.rBonusMax > rBonus ? ` - ${fmt(this.rBonusMax)}` : '';
      const bonusRow = rBonusPct > 0
        ? `<div class="pr-row"><span>${esc(t('bonus'))} ${rBonusPct}%</span><span>${esc(this.currencySymbol)} ${fmt(rBonus)}${bonusRange}</span></div>`
        : '';

      const winningRow = isRange
        ? `<div class="pr-row"><span>${esc(t('winning'))} min.</span><span>${esc(this.currencySymbol)} ${fmt(this.rWinningMin)}</span></div>` +
          `<div class="pr-row pr-row--total"><span>${esc(t('winning'))} max.</span><span>${esc(this.currencySymbol)} ${fmt(this.rWinningMax)}</span></div>`
        : `<div class="pr-row pr-row--total"><span>${esc(t('winning'))} ${esc(t('total'))}</span><span>${esc(this.currencySymbol)} ${fmt(rWinningTotal)}</span></div>`;

      const logoHtml = this.skinLogoUrl ? `<div class="pr-logo"><img src="${esc(this.skinLogoUrl)}" alt="logo" /></div>` : '';

      return `
        ${logoHtml}
        <div class="pr-header">
          <div class="pr-datetime">${esc(datetime)}</div>
        </div>
        <div class="pr-events">${eventsHtml}</div>
        <div class="pr-footer">
          <div class="pr-row"><span>${esc(t(bet.type))}</span><span>${odds.length} ev.</span></div>
          ${combinationsRow}
          <div class="pr-row"><span>${esc(t('amount'))}</span><span>${esc(this.currencySymbol)} ${fmt(rAmount)}</span></div>
          ${oddsRow}${bonusRow}${winningRow}
          ${this._generateBarcodeSvg(rCode)}
        </div>`;
    },

    _generateBarcodeSvg(code) {
      if (!code) return '';
      try {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        JsBarcode(svg, String(code), {
          format: 'CODE128',
          width: 1.5,
          height: 40,
          displayValue: true,
          fontSize: 11,
          margin: 4,
          background: '#ffffff',
          lineColor: '#000000',
        });
        return `<div class="pr-barcode-svg">${svg.outerHTML}</div>`;
      } catch (e) {
        return `<div class="pr-barcode">${String(code)}</div>`;
      }
    },

    _popupHtml(receiptHtml) {
      return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Ricevuta</title>
<style>
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { width: 220px; margin: 0 auto; padding: 5px; background: #fff; font-family: 'Courier New', Courier, monospace; font-size: var(--font-size-xl); color: #000; word-wrap: break-word; }
  .print-receipt-host, .print-receipt { display: block !important; position: static !important; visibility: visible !important; width: auto !important; height: auto !important; overflow: visible !important; }
  .pr-logo { text-align: center; margin-bottom: 6px; }
  .pr-logo img { max-width: 120px; max-height: 40px; object-fit: contain; }
  .pr-header { text-align: center; padding-bottom: 6px; border-bottom: 1px dashed #000; margin-bottom: 6px; }
  .pr-datetime { font-size: var(--font-size-md); }
  .pr-id { font-size: var(--font-size-xl); font-weight: bold; margin-top: 3px; }
  .pr-events { margin-bottom: 6px; }
  .pr-event { padding: 4px 0; border-bottom: 1px dashed #bbb; font-size: var(--font-size-md); }
  .pr-match { font-size: var(--font-size-xs); text-transform: uppercase; color: #555; }
  .pr-desc { font-size: var(--font-size-xl); font-weight: bold; margin: 2px 0; }
  .pr-sign { font-size: var(--font-size-md); margin: 2px 0; }
  .pr-line { display: flex; justify-content: space-between; gap: 4px; margin-top: 2px; }
  .pr-bb-odd { display: flex; justify-content: space-between; gap: 4px; padding: 1px 0; }
  .pr-odd-val { font-weight: bold; white-space: nowrap; }
  .pr-footer { border-top: 1px dashed #000; padding-top: 6px; margin-top: 4px; }
  .pr-row { display: flex; justify-content: space-between; padding: 2px 0; }
  .pr-divider { border-top: 1px dashed #bbb; margin: 4px 0; }
  .pr-row--total { font-weight: bold; font-size: var(--font-size-2xl); border-top: 1px dashed #000; margin-top: 4px; padding-top: 4px; }
  .pr-barcode { text-align: center; font-size: var(--font-size-3xl); font-weight: bold; letter-spacing: 4px; border: 1px solid #000; padding: 6px 4px; margin-top: 10px; word-break: break-all; }
  .pr-barcode-svg { text-align: center; margin-top: 10px; }
  .pr-barcode-svg svg { max-width: 100%; height: auto; }
  @media print {
    body { width: 58mm; }
    @page { size: 58mm auto; margin: 0; }
  }
</style>
</head>
<body>${receiptHtml}</body>
</html>`;
    },
  },
};
</script>

<style scoped>
.print-receipt-host {
  position: absolute;
  left: -9999px;
  top: 0;
  visibility: hidden;
  pointer-events: none;
  overflow: hidden;
  width: 220px;
}
.print-receipt {
  font-family: 'Courier New', Courier, monospace;
  font-size: var(--font-size-xl);
  color: #000;
  background: #fff;
  word-wrap: break-word;
  padding: 5px;
  box-sizing: border-box;
  width: 220px;
}
.pr-logo {
  text-align: center;
  margin-bottom: 6px;
}
.pr-logo img {
  max-width: 120px;
  max-height: 40px;
  object-fit: contain;
}
.pr-header {
  text-align: center;
  padding-bottom: 6px;
  border-bottom: 1px dashed #000;
  margin-bottom: 6px;
}
.pr-datetime { font-size: var(--font-size-md); }
.pr-id { font-size: var(--font-size-xl); font-weight: bold; margin-top: 3px; }
.pr-events { margin-bottom: 6px; }
.pr-event {
  padding: 4px 0;
  border-bottom: 1px dashed #bbb;
  font-size: var(--font-size-md);
}
.pr-match { font-size: var(--font-size-xs); text-transform: uppercase; color: #555; }
.pr-desc { font-size: var(--font-size-xl); font-weight: bold; margin: 2px 0; }
.pr-sign { font-size: var(--font-size-md); margin: 2px 0; }
.pr-line {
  display: flex;
  justify-content: space-between;
  gap: 4px;
  margin-top: 2px;
}
.pr-bb-odd {
  display: flex;
  justify-content: space-between;
  gap: 4px;
  padding: 1px 0;
}
.pr-odd-val { font-weight: bold; white-space: nowrap; }
.pr-footer {
  border-top: 1px dashed #000;
  padding-top: 6px;
  margin-top: 4px;
}
.pr-row {
  display: flex;
  justify-content: space-between;
  padding: 2px 0;
}
.pr-divider { border-top: 1px dashed #bbb; margin: 4px 0; }
.pr-row--total {
  font-weight: bold;
  font-size: var(--font-size-2xl);
  border-top: 1px dashed #000;
  margin-top: 4px;
  padding-top: 4px;
}
.pr-barcode-svg { text-align: center; margin-top: 10px; }
.pr-barcode-svg svg { max-width: 100%; height: auto; }
</style>
